import { type Team, type hfb, useSearchTeam } from "@repo/api";
import { Badge, Loading, Text, peopleIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import { hasItems, isDefined } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { useState } from "react";

import { DebouncedSearchField } from "../../DebouncedSearchField";
import { Line } from "../../Line";
import { SectionContainer } from "../../SectionContainer";
import { type SelectListItem, SelectListView } from "../../SelectListView";
import { SearchTeamFilters } from "./SearchTeamFilters";

interface SearchTeamProps {
  onSelect: (team: Team) => void;
}

const SearchTeamResult = ({
  searchResult,
  onSelect,
  selectedTeam,
}: {
  searchResult: Team[];
  selectedTeam?: Team;
  onSelect: (team: Team) => void;
}) => {
  const list: SelectListItem[] = searchResult.map((el) => ({
    value: String(el.teamId ?? ""),
    name: el.teamName ?? "",
    title: el.teamName,
    description: el.description
      ? `${el.description} | ${t("ID")}: ${el.teamId}`
      : `${t("label.ID")}: ${el.teamId}`,
    addOn: el.default ? (
      <Badge colour="blue" label={t("label.default_team")} size="small" />
    ) : undefined,
    icon: peopleIcon,
  }));

  const onChange = (_: boolean, item: SelectListItem) => {
    const selectedItem = searchResult.find(
      (i) => String(i.teamId) === item.value
    );

    if (selectedItem) {
      onSelect(selectedItem);
    }
  };

  return (
    <SelectListView
      id="search-team-result"
      list={list}
      onChange={onChange}
      selectedKeys={[
        selectedTeam ? String(selectedTeam?.teamId) : undefined,
      ].filter(isDefined)}
      selectionMode="SINGLE"
    />
  );
};

export const SearchTeam = ({ onSelect }: SearchTeamProps) => {
  const [selectedTeam, setSelectedTeam] = useState<Team | undefined>();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [buCodes, setBuCodes] = useState<string[]>();
  const [hfbs, setHfbs] = useState<hfb[]>();

  const { data: searchResult, isValidating } = useSearchTeam({
    searchTerm,
    hfbs,
    buCodes,
  });

  const hasSearchValue =
    !isEmpty(searchTerm) || hasItems(buCodes) || hasItems(hfbs);

  return (
    <SectionContainer>
      <DebouncedSearchField
        id="team-seach-input"
        value={searchTerm}
        placeholder={t("prompt.placeholder_search_team")}
        debounceTime={700}
        onChange={(value) => setSearchTerm(value)}
        fieldHelper={{
          id: "team-search-input-helper",
          msg: t("prompt.helper_text_search_team"),
        }}
      />
      <SearchTeamFilters
        buCodes={buCodes}
        hfbs={hfbs}
        setBuCodes={setBuCodes}
        setHfbs={setHfbs}
        clearAll={() => {
          setBuCodes(undefined);
          setHfbs(undefined);
        }}
      />

      {isValidating && <Loading text={t("prompt.loading")} />}
      {!isValidating && hasSearchValue && hasItems(searchResult) && (
        <div>
          <Text>
            {searchTerm
              ? t("prompt.search_result_for", {
                  searchTerm,
                })
              : t("prompt.search_result")}
          </Text>
          <SearchTeamResult
            searchResult={searchResult}
            onSelect={(team) => {
              setSelectedTeam(team);
              onSelect(team);
            }}
            selectedTeam={selectedTeam}
          />
        </div>
      )}

      {!isValidating && hasSearchValue && !hasItems(searchResult) && (
        <Text>
          {searchTerm
            ? t("prompt.no_result_for", {
                searchTerm,
              })
            : t("prompt.no_search_result")}
        </Text>
      )}

      {!hasItems(searchResult) && <Line />}
    </SectionContainer>
  );
};
